import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { changeClassRoom } from '@core/actions/classRoom';
import {
  goToEngagementInsights,
  goToMaterials,
  openMaterials,
} from '@app/actions/navigation';
import push from '@app/utils/routing';
import { SpaceWidgetCardData } from './SpaceWidget.types';

const switchSpace = async (
  dispatch: ReturnType<typeof useDispatch>,
  spaceId: string
) => {
  await dispatch(changeClassRoom(spaceId) as any);
};

export const useSpaceCardNavigation = (space: SpaceWidgetCardData) => {
  const dispatch = useDispatch();

  const handleTitleClick = useCallback(
    async (event: React.MouseEvent | React.KeyboardEvent) => {
      event.stopPropagation();
      await switchSpace(dispatch, space.id);
      push(`/dashboard/${space.id}/myspacenav`);
    },
    [dispatch, space.id]
  );

  const handleTitleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleTitleClick(event);
      }
    },
    [handleTitleClick]
  );

  const handleResumeClick = useCallback(async () => {
    await switchSpace(dispatch, space.id);

    if (space.role === 'student' && space.currentMaterialId) {
      dispatch(
        goToMaterials(space.id, space.currentMaterialId, { from: 'home' })
      );
      return;
    }

    if (space.role === 'student') {
      dispatch(goToMaterials(space.id, null, { from: 'home' }));
      return;
    }

    dispatch(openMaterials(space.id, { from: 'home' }));
  }, [dispatch, space]);

  const handleEngagementClick = useCallback(
    async (event: React.MouseEvent | React.KeyboardEvent) => {
      event.stopPropagation();
      await switchSpace(dispatch, space.id);
      dispatch(goToEngagementInsights(space.id, null, { from: 'myspacenav' }));
    },
    [dispatch, space.id]
  );

  const handleEngagementKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleEngagementClick(event);
      }
    },
    [handleEngagementClick]
  );

  return {
    handleTitleClick,
    handleTitleKeyDown,
    handleResumeClick,
    handleEngagementClick,
    handleEngagementKeyDown,
  };
};
